import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLazyFetchReportImagesQuery,
  useLazyFetchReportImageStreamQuery,
  useDeleteReportImageMutation
} from '@api/report-images/report-images-api';
import { useFetchToken } from '@hooks/useFetchToken';
import { ReportImage } from '@models/DomainModels/Master/report-image';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { logger } from '@utils/logging-utils';
import { CdfDeleteConfirmationDialog } from '@commonComponents/dialogs/CdfDeleteConfirmationDialog';
import { SearchTextField } from '@commonComponents/forms/fields/SearchTextField';
import { ImageGrid } from '../viewers/ImageGrid';
import { useCustomSnackbar } from '@utils/snackbar-utils';
import { ReportImageDialogWrapper } from '../dialogs/ReportImageDialog';
import { store } from '@state/dynamic-store';
import { FullSizeImageViewer } from '../viewers/FullSizeImageViewer';

/**
 * Props for ReportImagesComponent
 */
export interface ReportImagesComponentProps {
  /** Organization ID for API calls */
  readonly organizationId: string;
  /** Redux store for state management (optional, for future use) */
  readonly store?: unknown;
}

/**
 * Group report images by page section (header/footer)
 * @param images - Array of report images
 * @returns Object with Kopfdatei and Fussdatei arrays
 */
function groupImagesBySection(images: ReportImage[]): { header: ReportImage[]; footer: ReportImage[] } {
  return {
    header: images.filter(img => img.pageSection === 'Header'),
    footer: images.filter(img => img.pageSection === 'Footer')
  };
}

/**
 * Interface for image data cache (now base64 strings)
 */
interface ImageDataCache {
  [key: string]: string;
}

/**
 * Main component for displaying report images as cards, grouped by section.
 * @param props - ReportImagesComponentProps
 */
export const ReportImagesComponent: React.FC<Readonly<ReportImagesComponentProps>> = ({ organizationId }) => {
  const { t } = useTranslation(['reportImages', 'common']);
  const token = useFetchToken();
  const [fetchReportImages, { data: images = [], isLoading, isError }] = useLazyFetchReportImagesQuery();
  const [fetchImageStream] = useLazyFetchReportImageStreamQuery();
  const [deleteReportImage] = useDeleteReportImageMutation();
  const [imageData, setImageData] = useState<ImageDataCache>({});
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState<boolean>(false);
  const [imageToDelete, setImageToDelete] = useState<string>('');
  const [imageNameToDelete, setImageNameToDelete] = useState<string>('');
  const processingImagesRef = useRef<Set<string>>(new Set());
  const { showSnackbar } = useCustomSnackbar();

  // State for the ReportImageDialog
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentImage, setCurrentImage] = useState<ReportImage | undefined>(undefined);
  const [currentSection, setCurrentSection] = useState<'header' | 'footer'>('header');

  // State for full-size image viewer
  const [fullSizeImageVisible, setFullSizeImageVisible] = useState<boolean>(false);
  const [fullSizeImageData, setFullSizeImageData] = useState<string>('');
  const [fullSizeImageInfo, setFullSizeImageInfo] = useState<ReportImage | undefined>(undefined);
  const [loadingFullSizeImage, setLoadingFullSizeImage] = useState<boolean>(false);

  /**
   * Delete a single image and return result
   */
  const deleteSingleImage = useCallback(
    async (imageId: string): Promise<{ success: boolean; imageName: string }> => {
      const imageName = images.find(img => img.id === imageId)?.name ?? imageId;

      try {
        await deleteReportImage({
          organizationId,
          reportImageId: imageId,
          token
        }).unwrap();

        setImageData(prev => {
          const updated = { ...prev };
          delete updated[imageId];
          return updated;
        });

        return { success: true, imageName };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('ReportImagesComponent', `Failed to delete image ${imageId}:`, errorMessage);
        return { success: false, imageName };
      }
    },
    [deleteReportImage, organizationId, token, images]
  );

  // First useEffect - Fetch report images
  useEffect(() => {
    if (token && organizationId) {
      fetchReportImages({ organizationId, token });
    }
  }, [fetchReportImages, organizationId, token]);

  // Replace the filter useEffect with useMemo
  const filteredImages = useMemo(() => {
    if (!searchTerm.trim()) {
      return images;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      return images.filter(img => img.name.toLowerCase().includes(lowerSearchTerm));
    }
  }, [images, searchTerm]);

  const { header, footer } = groupImagesBySection(filteredImages);

  /**
   * Fetch image data by ID using the RTK Query endpoint
   * Now returns base64 string directly
   */
  const loadImageData = useCallback(
    async (imageId: string) => {
      if (!token || !imageId || imageData[imageId] || loadingImages.has(imageId)) {
        return;
      }

      try {
        setLoadingImages(prev => new Set(prev).add(imageId));

        const base64Data = await fetchImageStream({
          reportImageId: imageId,
          organizationId,
          token
        }).unwrap();

        setImageData(prev => ({
          ...prev,
          [imageId]: base64Data
        }));
      } catch (error) {
        logger.error('ReportImagesComponent', `Error loading image data: ${error instanceof Error ? error.message : String(error)}`);
        showSnackbar(t('reportImages.errorLoadingImageSingle'), 'error');
      } finally {
        setLoadingImages(prev => {
          const updated = new Set(prev);
          updated.delete(imageId);
          return updated;
        });
      }
    },
    [token, imageData, loadingImages, fetchImageStream, organizationId, t, showSnackbar]
  );

  // Third useEffect - Load image data for all images
  useEffect(() => {
    if (images.length === 0 || !token) {
      return;
    }

    // Get IDs of images that don't have data yet and aren't being processed
    const missingImageIds = images
      .filter(img => {
        const isLoaded = !!imageData[img.id];
        const isLoading = loadingImages.has(img.id);
        const isProcessing = processingImagesRef.current.has(img.id);
        return !isLoaded && !isLoading && !isProcessing;
      })
      .map(img => img.id);

    if (missingImageIds.length === 0) {
      return;
    }

    // Fetch each missing image (limit concurrent requests)
    const imagesToLoad = missingImageIds.slice(0, 5);

    if (imagesToLoad.length > 0) {
      // Mark as processing in ref
      imagesToLoad.forEach(id => {
        processingImagesRef.current.add(id);
        loadImageData(id).finally(() => {
          // Clean up processing ref when done
          processingImagesRef.current.delete(id);
        });
      });
    }
  }, [images, token, loadImageData, imageData, loadingImages]);

  /**
   * Handler for add button (opens dialog, to be implemented)
   * @param section - 'header' | 'footer'
   */
  const handleAddClick = useCallback((section: 'header' | 'footer') => {
    logger.info('Open add dialog for', section);
    // Placeholder for dialog logic
    setCurrentSection(section);
    setDialogMode('add');
    setCurrentImage(undefined);
    setIsDialogVisible(true);
  }, []);

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  /**
   * Handle actual deletion after confirmation from the dialog.
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!imageToDelete) {
      return;
    }

    const result = await deleteSingleImage(imageToDelete);

    if (result.success) {
      showSnackbar(t('reportImages.deleteSuccessSingle', { imageName: result.imageName }), 'success');
      logger.info('ReportImagesComponent', 'Successfully deleted image:', imageToDelete);
      if (token && organizationId) {
        fetchReportImages({ organizationId, token });
      }
    } else {
      showSnackbar(t('reportImages.deleteErrorSingle', { imageName: result.imageName }), 'error');
      logger.error('ReportImagesComponent', 'Failed to delete image:', imageToDelete);
    }

    setShowDeleteConfirmDialog(false);
    setImageToDelete('');
    setImageNameToDelete('');
  }, [imageToDelete, deleteSingleImage, showSnackbar, t, token, organizationId, fetchReportImages]);

  const handleCloseDeleteDialog = useCallback(() => {
    setShowDeleteConfirmDialog(false);
    setImageToDelete('');
    setImageNameToDelete('');
  }, []);

  /**
   * Handle deletion of a single image directly from the ImageGrid/ImageCard.
   * This will now trigger the confirmation dialog.
   * @param imageId The ID of the image to delete.
   */
  const handleDeleteImageFromGrid = useCallback(
    (imageId: string) => {
      if (!imageId) {
        return;
      }
      const imageToDeleteObj = images.find(img => img.id === imageId);
      const imageName = imageToDeleteObj?.name ?? imageId;

      setImageToDelete(imageId);
      setImageNameToDelete(imageName);
      setShowDeleteConfirmDialog(true);
    },
    [images]
  );

  // Dialog Handlers
  const handleDialogClose = useCallback(() => {
    setIsDialogVisible(false);
    setCurrentImage(undefined);
  }, []);

  const handleDialogSuccess = useCallback((imageId?: string) => {
    // Called when the dialog successfully adds or edits an image
    logger.info('Dialog operation successful', 'Refetching images after add/edit.', { imageId });
    
    // Clear cached image data for the updated image to force reload with new data
    // Use imageId parameter if provided (from ImageCard edit), otherwise use currentImage (from main dialog)
    const idToClear = imageId ?? currentImage?.id;
    if (idToClear) {
      setImageData(prev => {
        const updated = { ...prev };
        delete updated[idToClear];
        return updated;
      });
      // Also clear from loading set if present
      setLoadingImages(prev => {
        const updated = new Set(prev);
        updated.delete(idToClear);
        return updated;
      });
      // Also clear from processing ref
      processingImagesRef.current.delete(idToClear);
    }
    
    setIsDialogVisible(false); // Close the dialog
    setCurrentImage(undefined); // Clear current image
    if (token && organizationId) {
      fetchReportImages({ organizationId, token });
    }
  }, [token, organizationId, fetchReportImages, currentImage]);

  /**
   * Handle viewing an image in full size
   * @param image - The image to view in full size
   */
  const handleViewFullSize = useCallback(
    async (image: ReportImage) => {
      if (!token || !image.id) {
        return;
      }

      try {
        setLoadingFullSizeImage(true);
        setFullSizeImageInfo(image);
        setFullSizeImageVisible(true);

        // Fetch the original full-size image
        const fullSizeBase64Data = await fetchImageStream({
          reportImageId: image.id,
          organizationId,
          token,
          showFullImage: true // This will fetch the original image
        }).unwrap();

        setFullSizeImageData(fullSizeBase64Data);
      } catch (error) {
        logger.error('ReportImagesComponent', `Error loading full-size image: ${error instanceof Error ? error.message : String(error)}`);
        showSnackbar(t('reportImages.errorLoadingImageSingle'), 'error');
        setFullSizeImageVisible(false);
      } finally {
        setLoadingFullSizeImage(false);
      }
    },
    [token, fetchImageStream, organizationId, t, showSnackbar]
  );

  /**
   * Handle closing the full-size image viewer
   */
  const handleCloseFullSizeViewer = useCallback(() => {
    setFullSizeImageVisible(false);
    setFullSizeImageData('');
    setFullSizeImageInfo(undefined);
    setLoadingFullSizeImage(false);
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress data-testid="loading-spinner" />
      </Box>
    );
  }
  if (isError) {
    return <Typography color="error">{t('errorLoadingImages', { ns: 'reportImages' })}</Typography>;
  }

  return (
    <Paper>
      <SearchTextField onChange={handleSearchChange} />

      <ImageGrid
        sectionKey="header"
        sectionLabel={t('reportImages.headerSection')}
        images={header}
        imageData={imageData}
        loadingImages={loadingImages}
        onAddClick={handleAddClick}
        onLoadImage={loadImageData}
        store={store}
        organizationId={organizationId}
        onDeleteImage={handleDeleteImageFromGrid}
        onViewFullSize={handleViewFullSize}
        onDialogSuccess={handleDialogSuccess}
      />

      <ImageGrid
        sectionKey="footer"
        sectionLabel={t('reportImages.footerSection')}
        images={footer}
        imageData={imageData}
        loadingImages={loadingImages}
        onAddClick={handleAddClick}
        onLoadImage={loadImageData}
        store={store}
        organizationId={organizationId}
        onDeleteImage={handleDeleteImageFromGrid}
        onViewFullSize={handleViewFullSize}
        onDialogSuccess={handleDialogSuccess}
      />

      {/* Delete confirmation dialog */}
      <CdfDeleteConfirmationDialog
        open={showDeleteConfirmDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        itemName={imageNameToDelete}
      />

      {/* Add/Edit Image Dialog */}
      {isDialogVisible && (
        <ReportImageDialogWrapper
          open={isDialogVisible}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
          initialData={currentImage}
          section={currentSection}
          organizationId={organizationId}
          store={store}
          dialogMode={dialogMode}
        />
      )}

      {/* Full-size image viewer */}
      <FullSizeImageViewer
        open={fullSizeImageVisible}
        imageData={fullSizeImageData}
        imageInfo={fullSizeImageInfo}
        loading={loadingFullSizeImage}
        onClose={handleCloseFullSizeViewer}
      />
    </Paper>
  );
};
